import { Box, useFieldContext } from '@chakra-ui/react';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VCheckbox from '@voly/ui-components/src/v2/VCheckbox';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import { useEditor, type Editor, type JSONContent } from '@tiptap/react';
import {
  Control,
  createBooleanControl,
  RichTextEditor,
} from '@voly/ui-components/src/v2/VRichText/VRichTextEditor';
import {
  filterMentionUsers,
  type TMentionUser,
} from '@voly/ui-components/src/v2/VRichText/VRichTextMention';
import { createMentionSuggestion } from '@voly/ui-components/src/v2/VMentionInput/mentionSuggestion.jsx';
import { FocusEvent, MouseEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { LuAtSign } from 'react-icons/lu';
import { translate } from 'src/app/project/features/translate/translate';
import { createPointFieldExtensions } from '../../utils/richTextExtensions';
import {
  decodeRichTextValue,
  encodeRichTextDocument,
  encodeRichTextValue,
  readMentionIds,
} from '../../utils/richTextValue';

const CHARACTER_COUNTER_THRESHOLD = 500;

type TStoredValue = {
  content: string;
  encoded: string;
};

const createSuggestionContainer = (): HTMLDivElement => {
  const element = document.createElement('div');

  element.style.position = 'fixed';
  element.style.top = '0';
  element.style.left = '0';
  element.style.zIndex = '99999999';

  return element;
};

const removeId = (ids: string[], removedId: string): string[] => {
  const index = ids.indexOf(removedId);

  if (index === -1) {
    return ids;
  }

  return [...ids.slice(0, index), ...ids.slice(index + 1)];
};

const addedMentionIds = (storedValue: string, nextValue: string): string[] =>
  readMentionIds(storedValue).reduce(removeId, readMentionIds(nextValue));

const readStoredValue = (value: string): TStoredValue => {
  const content = decodeRichTextValue(value);

  return { content, encoded: encodeRichTextValue(content) };
};

const MentionControl = createBooleanControl({
  label: 'Mention',
  icon: LuAtSign,
  command: (editor) => {
    const { from } = editor.state.selection;
    const characterBefore = editor.state.doc.textBetween(Math.max(from - 1, 0), from);
    const separator = characterBefore.trim() ? ' ' : '';

    editor.chain().focus().insertContent(`${separator}@`).run();
  },
  getVariant: () => 'ghost',
});

interface StorableToolsProps {
  hasMentions: boolean;
  onMouseDown: (event: MouseEvent) => void;
}

const StorableTools = ({ hasMentions, onMouseDown }: StorableToolsProps): ReactElement => (
  <VStackRow gap={1} padding={1} borderBottomWidth="1px" onMouseDown={onMouseDown}>
    <RichTextEditor.ControlGroup>
      <Control.Bold />
      <Control.Italic />
      <Control.Underline />
      <Control.Strikethrough />
      <Control.Link />
      {hasMentions && <MentionControl />}
    </RichTextEditor.ControlGroup>

    <RichTextEditor.ControlGroup>
      <Control.BulletList />
      <Control.OrderedList />
    </RichTextEditor.ControlGroup>
  </VStackRow>
);

interface CharacterCounterProps {
  editor: Editor;
  limit?: number;
}

const CharacterCounter = ({ editor, limit }: CharacterCounterProps): ReactElement | null => {
  if (!limit) {
    return null;
  }

  const count = editor.storage.characterCount.characters();
  const remaining = limit - count;

  if (remaining >= CHARACTER_COUNTER_THRESHOLD) {
    return null;
  }

  return (
    <Box
      data-testid="rich-text-character-count"
      textAlign="end"
      fontSize="xs"
      paddingX={2}
      paddingBottom={1}
      color={remaining < 0 ? 'fg.error' : 'fg.muted'}
    >
      {count} / {limit}
    </Box>
  );
};

export type TRichTextSubmitOnEnter = {
  value: boolean;
  onToggle: () => void;
};

export type TRichTextActions = {
  submitLabel: string;
  submitOnEnter: TRichTextSubmitOnEnter;
  onSubmit: (value: string, mentionIds: string[]) => void;
  onEmptySubmit: () => void;
};

type TRichTextFieldBaseProps = {
  value: string;
  readOnly: boolean;
  placeholder?: string;
  showPlaceholderWhenReadOnly?: boolean;
  characterLimit?: number;
  mentions?: TMentionUser[];
  startElement?: ReactElement;
};

type TSaveOnBlurProps = {
  onSave: (value: string, mentionIds: string[]) => void;
  actions?: undefined;
};

type TSubmitProps = {
  actions: TRichTextActions;
  onSave?: undefined;
};

export type RichTextFieldSaveProps = TRichTextFieldBaseProps & TSaveOnBlurProps;

export type RichTextFieldProps = RichTextFieldSaveProps | (TRichTextFieldBaseProps & TSubmitProps);

interface ActionBarProps {
  actions: TRichTextActions;
  canSubmit: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  onToggleSubmitOnEnter: () => void;
  onMouseDown: (event: MouseEvent) => void;
}

const ActionBar = ({
  actions,
  canSubmit,
  onSubmit,
  onCancel,
  onToggleSubmitOnEnter,
  onMouseDown,
}: ActionBarProps): ReactElement => (
  <VStackRow
    data-testid="rich-text-actions"
    justifyContent="space-between"
    padding={2}
    borderTopWidth="1px"
    onMouseDown={onMouseDown}
  >
    <VCheckbox checked={actions.submitOnEnter.value} onCheckedChange={onToggleSubmitOnEnter}>
      {translate('press_enter_to_send')}
    </VCheckbox>
    <VStackRow gap={2}>
      <VButton color="secondary" onClick={onCancel}>
        {translate('cancel')}
      </VButton>
      <VButton onClick={onSubmit} disabled={!canSubmit}>
        {actions.submitLabel}
      </VButton>
    </VStackRow>
  </VStackRow>
);

const RichTextField = ({
  value,
  readOnly,
  placeholder,
  showPlaceholderWhenReadOnly = false,
  characterLimit,
  mentions,
  startElement,
  onSave,
  actions,
}: RichTextFieldProps): ReactElement | null => {
  const fieldDisabled: boolean = useFieldContext()?.disabled ?? false;
  const editable: boolean = !readOnly && !fieldDisabled;
  const stored = useMemo(() => readStoredValue(value), [value]);
  const hasMentions = Boolean(mentions?.length);

  const hostRef = useRef<HTMLDivElement>(null);
  const mentionsRef = useRef(mentions);
  const actionsRef = useRef(actions);
  const [suggestionContainer] = useState(createSuggestionContainer);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    mentionsRef.current = mentions;
  }, [mentions]);

  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  const searchMentions = (query: string): TMentionUser[] =>
    filterMentionUsers(mentionsRef.current ?? [], query);

  const commit = (content: JSONContent): void => {
    if (!onSave) {
      return;
    }

    const nextValue = encodeRichTextDocument(content);

    if (nextValue === stored.encoded) {
      return;
    }

    onSave(nextValue, addedMentionIds(stored.encoded, nextValue));
  };

  const submitFrom = (submittingEditor: Editor): boolean => {
    const currentActions = actionsRef.current;
    const nextValue = encodeRichTextDocument(submittingEditor.getJSON());

    if (!currentActions) {
      return false;
    }

    if (nextValue === '') {
      currentActions.onEmptySubmit();

      return true;
    }

    currentActions.onSubmit(nextValue, readMentionIds(nextValue));
    submittingEditor.chain().clearContent(true).blur().run();

    return true;
  };

  const editor = useEditor({
    extensions: createPointFieldExtensions({
      placeholder,
      showPlaceholderWhenReadOnly,
      characterLimit,
      mentionSuggestion: {
        ...createMentionSuggestion(searchMentions, suggestionContainer),
        minQueryLength: 0,
        debounce: 0,
        allowedPrefixes: null,
      },
      ...(actions
        ? {
            onEnter: (submittingEditor: Editor): boolean =>
              actionsRef.current?.submitOnEnter.value === true
                ? submitFrom(submittingEditor)
                : false,
          }
        : {}),
    }),
    content: stored.content,
    editable,
    shouldRerenderOnTransaction: true,
    immediatelyRender: false,
    onBlur: ({ editor: blurredEditor }) => {
      commit(blurredEditor.getJSON());
    },
  });

  useEffect(() => {
    hostRef.current?.appendChild(suggestionContainer);

    return () => suggestionContainer.remove();
  }, [editor, suggestionContainer]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => {
    if (!editor || editor.isFocused) {
      return;
    }

    editor.commands.setContent(stored.content, { emitUpdate: false });
  }, [editor, stored.content]);

  if (!editor) {
    return null;
  }

  const keepEditorFocused = (event: MouseEvent): void => event.preventDefault();
  const collapseWhenFocusLeaves = (event: FocusEvent): void => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsFocused(false);
  };
  const isExpanded = isFocused || (actions !== undefined && !editor.isEmpty);
  const overLimit =
    characterLimit !== undefined && editor.storage.characterCount.characters() > characterLimit;
  const canSubmit = !editor.isEmpty && !overLimit;
  const cancel = (): void => {
    editor.chain().clearContent(true).blur().run();
  };
  const toggleSubmitOnEnter = (): void => {
    actions?.submitOnEnter.onToggle();
    editor.commands.focus();
  };

  return (
    <Box
      ref={hostRef}
      display="flex"
      alignItems="center"
      position="relative"
      width="100%"
      onFocus={() => setIsFocused(true)}
      onBlur={collapseWhenFocusLeaves}
    >
      <RichTextEditor.Root
        editor={editor}
        disabled={fieldDisabled}
        readOnly={readOnly}
        border="1px solid"
        borderColor="border"
        rounded="md"
        width="100%"
      >
        {isExpanded && <StorableTools hasMentions={hasMentions} onMouseDown={keepEditorFocused} />}

        <Box paddingStart={startElement ? '6' : undefined}>
          <RichTextEditor.Content />
        </Box>

        <RichTextEditor.LinkBar />

        <CharacterCounter editor={editor} limit={characterLimit} />

        {actions && isExpanded ? (
          <ActionBar
            actions={actions}
            canSubmit={canSubmit}
            onSubmit={() => submitFrom(editor)}
            onCancel={cancel}
            onToggleSubmitOnEnter={toggleSubmitOnEnter}
            onMouseDown={keepEditorFocused}
          />
        ) : null}
      </RichTextEditor.Root>

      {startElement ? (
        <Box position="absolute" insetStart={'3'}>
          {startElement}
        </Box>
      ) : null}
    </Box>
  );
};

export default RichTextField;
